/**
 * PeopleActivity Component
 * 
 * Displays activity timeline for a specific person.
 * Reuses ActivityTimeline component but with filtered data for the person.
 */

import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Form, Row, Col, Offcanvas, InputGroup } from 'react-bootstrap';
import Select from 'react-select';
import Flatpickr from 'react-flatpickr';
import ActivityTimeline from '../../../pages/Projects/Detail/activities/ActivityTimeline';
import '../../../pages/Projects/Detail/activities/Activities.css';

const PeopleActivity = () => {
    const { peopleId } = useParams();
    
    const [showFilter, setShowFilter] = useState(false);
    const [selectedActivityType, setSelectedActivityType] = useState(null);
    const [selectedChangesBy, setSelectedChangesBy] = useState(null);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [selectedPriority, setSelectedPriority] = useState(null);
    const [selectedProjectStatus, setSelectedProjectStatus] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const activityTypeOptions = [
        { value: 'all', label: 'All Activities' },
        { value: 'project', label: 'Project Updates' },
        { value: 'task', label: 'Task Updates' },
        { value: 'subtask', label: 'Subtask Updates' },
        { value: 'comment', label: 'Comments' },
        { value: 'file', label: 'File & Attachments' },
        { value: 'member', label: 'Team Changes' },
        { value: 'time', label: 'Time Tracking' },
        { value: 'workflow', label: 'Workflow Changes' },
        { value: 'milestone', label: 'Milestones' },
        { value: 'notification', label: 'Notifications' },
        { value: 'financial', label: 'Financial Updates' },
        { value: 'risk', label: 'Risk & Issues' },
        { value: 'approval', label: 'Approvals' },
        { value: 'dependency', label: 'Dependencies' },
        { value: 'report', label: 'Reports' },
        { value: 'tag', label: 'Tags & Labels' },
        { value: 'integration', label: 'Integrations' },
        { value: 'security', label: 'Security' },
        { value: 'template', label: 'Templates' },
        { value: 'other', label: 'Other' }
    ];

    const changesByOptions = [
        { value: 'john-doe', label: 'John Doe' },
        { value: 'jane-smith', label: 'Jane Smith' },
        { value: 'mike-johnson', label: 'Mike Johnson' },
        { value: 'sarah-wilson', label: 'Sarah Wilson' },
        { value: 'david-brown', label: 'David Brown' }
    ];

    const priorityOptions = [
        { value: 'high', label: 'High' },
        { value: 'medium', label: 'Medium' },
        { value: 'low', label: 'Low' }
    ];

    const projectStatusOptions = [
        { value: 'active', label: 'Active' },
        { value: 'completed', label: 'Completed' },
        { value: 'on-hold', label: 'On Hold' },
        { value: 'cancelled', label: 'Cancelled' }
    ];

    const handleResetFilters = () => {
        setSelectedActivityType(null);
        setSelectedChangesBy(null);
        setStartDate(null);
        setEndDate(null);
        setSelectedPriority(null);
        setSelectedProjectStatus(null);
        setSearchQuery('');
    };

    // Mock activities data - NOTE: In real implementation, filter by peopleId
    const mockActivities = [
        { id: 1, user: { name: 'John Doe', avatar: 'https://randomuser.me/api/portraits/men/32.jpg', initials: 'JD' }, action: 'Created Task', type: 'task', title: 'Homepage Design Mockup', description: 'Created task in Development task list', timestamp: '2024-12-01T10:30:00', metadata: { taskList: 'Development' } },
        { id: 2, user: { name: 'John Doe', avatar: 'https://randomuser.me/api/portraits/men/32.jpg', initials: 'JD' }, action: 'Updated Task', type: 'task', title: 'API Integration', description: 'Changed priority from Medium to High', timestamp: '2024-12-01T09:45:00', metadata: { changes: [{ field: 'Priority', from: 'Medium', to: 'High' }] } },
        { id: 3, user: { name: 'John Doe', avatar: 'https://randomuser.me/api/portraits/men/32.jpg', initials: 'JD' }, action: 'Logged Time', type: 'time', title: 'API Development', description: 'Logged 3 hours: Development of authentication module', timestamp: '2024-11-30T15:30:00', metadata: { hours: '3h', date: 'Nov 30, 2024' } },
        { id: 4, user: { name: 'John Doe', avatar: 'https://randomuser.me/api/portraits/men/32.jpg', initials: 'JD' }, action: 'Added Comment', type: 'comment', title: 'Bug Fix #234', description: 'Fixed the authentication issue in login module', timestamp: '2024-11-30T14:15:00', metadata: null },
        { id: 5, user: { name: 'John Doe', avatar: 'https://randomuser.me/api/portraits/men/32.jpg', initials: 'JD' }, action: 'Uploaded File', type: 'file', title: 'Design Assets', description: 'Uploaded logo-variants.zip (2.5 MB)', timestamp: '2024-11-30T12:30:00', metadata: { fileCount: 1, totalSize: '2.5 MB' } },
        { id: 6, user: { name: 'John Doe', avatar: 'https://randomuser.me/api/portraits/men/32.jpg', initials: 'JD' }, action: 'Changed Task Status', type: 'task', title: 'Database Schema Design', description: null, timestamp: '2024-11-30T11:00:00', metadata: { from: 'In Progress', to: 'Completed' } },
        { id: 7, user: { name: 'John Doe', avatar: 'https://randomuser.me/api/portraits/men/32.jpg', initials: 'JD' }, action: 'Created Subtask', type: 'subtask', title: 'Mobile App Features', description: 'Added subtask "Push notification setup"', timestamp: '2024-11-29T16:45:00', metadata: { subtask: 'Push notification setup' } },
        { id: 8, user: { name: 'John Doe', avatar: 'https://randomuser.me/api/portraits/men/32.jpg', initials: 'JD' }, action: 'Started Timer', type: 'time', title: 'Code Review', description: 'Started task timer', timestamp: '2024-11-29T15:00:00', metadata: { action: 'start' } },
    ];

    console.log('Loading activities for person:', peopleId);

    return (
        <>
            <div className='c-card-1'>
                <div className='c-card-header-1'>
                    <div className='d-flex justify-content-between align-items-center w-100'>
                        <div>Activity Timeline</div>
                        <div className='d-flex align-items-center gap-2'>
                            <Form.Control
                                type="text"
                                placeholder="Search activities..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                style={{ maxWidth: '250px' }}
                                className="me-2"
                            />
                            <Button 
                                variant='outline-primary' 
                                className='btn-sm text-nowrap'
                                onClick={() => setShowFilter(true)}
                            >
                                <i className="fas fa-filter me-1"></i>
                                Filter
                            </Button>
                        </div>
                    </div>
                </div>
                <div className='c-card-body-1'>
                    <ActivityTimeline 
                        activities={mockActivities}
                        peopleId={peopleId}
                    />
                </div>
            </div>

            {/* Filter Offcanvas */}
            <Offcanvas show={showFilter} onHide={() => setShowFilter(false)} placement="end">
                <Offcanvas.Header className='px-4 py-3' closeButton>
                    <Offcanvas.Title className='display-1'>Filter Activities</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body className='px-4 py-3'>
                    <Form>
                        <Row className='gy-3'>
                            <Col sm={12}>
                                <Form.Group>
                                    <Form.Label>Activity Type</Form.Label>
                                    <Select
                                        value={selectedActivityType}
                                        onChange={setSelectedActivityType}
                                        options={activityTypeOptions}
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                        placeholder="Select activity type..."
                                        isClearable
                                        isMulti
                                    />
                                </Form.Group>
                            </Col>
                            <Col sm={12}>
                                <Form.Group>
                                    <Form.Label>Changed By</Form.Label>
                                    <Select
                                        value={selectedChangesBy}
                                        onChange={setSelectedChangesBy}
                                        options={changesByOptions}
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                        placeholder="Select user..."
                                        isClearable
                                        isMulti
                                    />
                                </Form.Group>
                            </Col>
                            <Col sm={12}>
                                <Form.Group>
                                    <Form.Label>Date Range</Form.Label>
                                    <InputGroup>
                                        <Flatpickr
                                            value={startDate}
                                            onChange={([date]) => setStartDate(date)}
                                            className="form-control"
                                            options={{
                                                dateFormat: 'd M Y',
                                                maxDate: endDate || 'today',
                                                allowInput: true,
                                                disableMobile: true,
                                            }}
                                            placeholder="Start date"
                                        />
                                        <InputGroup.Text className="bg-light p-tag">to</InputGroup.Text>
                                        <Flatpickr
                                            value={endDate}
                                            onChange={([date]) => setEndDate(date)}
                                            className="form-control"
                                            options={{
                                                dateFormat: 'd M Y',
                                                minDate: startDate,
                                                maxDate: 'today',
                                                allowInput: true,
                                                disableMobile: true,
                                            }}
                                            placeholder="End date"
                                        />
                                    </InputGroup>
                                </Form.Group>
                            </Col>
                            <Col sm={12}>
                                <Form.Group>
                                    <Form.Label>Priority</Form.Label>
                                    <Select
                                        value={selectedPriority}
                                        onChange={setSelectedPriority}
                                        options={priorityOptions}
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                        placeholder="Select priority..."
                                        isClearable
                                        isMulti
                                    />
                                </Form.Group>
                            </Col>
                            <Col sm={12}>
                                <Form.Group>
                                    <Form.Label>Project Status</Form.Label>
                                    <Select
                                        value={selectedProjectStatus}
                                        onChange={setSelectedProjectStatus}
                                        options={projectStatusOptions}
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                        placeholder="Select status..."
                                        isClearable
                                        isMulti
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>
                </Offcanvas.Body>
                <div className="p-3 border-top">
                    <div className="d-flex justify-content-between">
                        <Button variant="link" className='text-decoration-none' onClick={handleResetFilters}>
                            Reset
                        </Button>
                        <Button variant="primary" onClick={() => setShowFilter(false)}>
                            Apply Filters
                        </Button>
                    </div>
                </div>
            </Offcanvas>
        </>
    );
};

export default PeopleActivity;
